/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Vu Dinh Luan (vdluan@rsupartners.com)
 *
 * @description
 *
 * @created at Mon Dec 18 2024
 **/

import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";

import {
	ChannelCreateSystemChat,
	CreateSystemChat,
	NameUpdateSystemChat,
	VoiceNoResponseSystemChat,
	VoiceSystemChat
} from "./private/components/SystemRendererItem";

interface ChatSystemRendererProps {
	systemForm: ChatServices.Format2.Elements.AllSystemElements;
	variant: ChatUI.Size;
}

export default function ChatSystemRenderer({
	systemForm,
	variant,
	...message
}: ChatSystemRendererProps & Partial<Omit<WebServices.Chat.Messages.Data["content"][number], "content">>) {
	if (!message.roomId) {
		return null;
	}

	switch (systemForm.key) {
		case "add": {
			return <CreateSystemChat type="add" systemForm={systemForm} createAt={message.createdAt} />;
		}
		case "create": {
			return <CreateSystemChat type="create" systemForm={systemForm} createAt={message.createdAt} />;
		}
		case "channelCreate": {
			return <ChannelCreateSystemChat systemForm={systemForm} variant={variant} />;
		}
		case "nameUpdate": {
			return <NameUpdateSystemChat systemForm={systemForm} />;
		}
		case "voiceChatStart": {
			return (
				<VoiceSystemChat
					message={_T("보이스챗 진행중")}
					variant={variant}
					createAt={message.createdAt}
					type="start"
				/>
			);
		}
		case "voiceChatEnd": {
			return (
				<VoiceSystemChat
					message={_T("보이스챗 종료됨")}
					variant={variant}
					createAt={message.createdAt}
					type="end"
					data={systemForm.data}
				/>
			);
		}
		case "voiceChatJoin": {
			if (variant === "transparent") return;
			return (
				<VoiceSystemChat
					message={_T("보이스챗 참여중")}
					variant={variant}
					createAt={message.createdAt}
					type="join"
				/>
			);
		}
		case "hangupNoResponseCall": {
			if (variant === "transparent") return;
			if (systemForm.data.caller.id === Configure.userID) return;
			return (
				<VoiceNoResponseSystemChat
					variant={variant}
					createAt={message.createdAt}
					caller={{ id: systemForm.data.caller.id || "", name: systemForm.data.caller.name || "" }}
					receiver={{ id: systemForm.data.receiver.id || "", name: systemForm.data.receiver.name || "" }}
					callUsers={systemForm.data.callUsers || []}
				/>
			);
		}
	}
}
